import { ActionTree } from 'vuex'
import { BlogState } from '@/modules/blog/store'
import { RootState } from '@/store/types'
import {Post, PostTranslation, PostPaginationFilters, PostPagination, Tag} from '@/modules/blog/types'
import * as api from '@/modules/blog/api'
import { MutationTypes } from '@/modules/blog/store/mutations'
import Locale from '@/modules/i18n/model/Locale'
import {notify, Colors} from '@/modules/core/notification'
import { Metadata } from '@/modules/seo/types';
import _ from 'lodash'

function getCurrentSEO (state: RootState): Metadata {
    return _.get(state, 'seo.currentMetadata', {})
}

const actions: ActionTree<BlogState, RootState> = {

    /**
     * Cargar publicación
     *
     * Obtiene una publicación del blog de Artisan, opcionalmente se puede definir
     * payload.editing a true, para que defina dicho artículo como publicación
     * actualmente en edición
     *
     * @param {any} commit
     * @param {any} dispatch
     * @param {any} rootGetters
     * @param {{post: number; editing: boolean}} payload
     * @returns {Promise<Post>}
     */
    loadPost: async ({commit, dispatch, rootGetters}, payload: {post: number, editing: boolean}): Promise<Post> => {
        const post = await api.getPost(payload.post)

        // Se carga en el estado la lista de localismos para poder asociar cada traducción
        // await dispatch('i18n/loadLocales', {}, {root: true})
        // post.translations.forEach(t =>
        //     t.locale =  rootGetters['i18n/locale'](t.locale),
        // )

        if (payload.editing) {
            commit(MutationTypes.SetEditingPost, post)
            commit(MutationTypes.SetEditingPostCurrentTranslation, 'es')
            // Carga el reporte de análisis SEO de la publicación
            await dispatch('seo/loadSEOAnalysisReport', null, { root: true })
        }

        return post
    },

    /**
     * Construir nueva publicación
     *
     * Construye una nueva entidad Post con una traducción con el localismo
     * por defecto de Artisan y cuyo autor es el usuario actual
     * de la sesión
     *
     * Si se activa payload.inEdition la nueva publicación será registrada
     * en el estado como publicación en edición actualmente.
     */
    buildPost: async (context, payload: { inEdition: boolean }): Promise<Post> => {
        const defaultLocale: Locale = await context.dispatch('i18n/loadDefaultLocale', {}, {root: true})
        const user = context.rootState.auth!.user

        // Nueva publicación
        const post  = new Post()
        post.author = {name: user!.name, id: user!.id}
        post.translations.push(new PostTranslation(defaultLocale.identifier))

        if (payload.inEdition) {
            context.commit(MutationTypes.SetEditingPost, post)
            context.commit(MutationTypes.SetEditingPostCurrentTranslation, defaultLocale.identifier)
        }

        return post
    },

    /**
     * Crea o actualiza la publicación actualmente en edición
     */
    savePost: async ({rootState, state, commit, dispatch}): Promise<Post> => {
        // Actualiza los metadatos SEO antes de actualizar la entidad
        commit(MutationTypes.UpdateCurrentPostField, {
            path: 'seo',
            value: getCurrentSEO(rootState),
        })

        const post: Post = state.editingPost.isNew
            ? await api.createPost(state.editingPost)
            : await api.updatePost(state.editingPost)

        commit(MutationTypes.SetEditingPost, post)

        await dispatch('seo/loadSEOAnalysisReport', null, { root: true })

        return post
    },

    /**
     * Cargar paginación
     *
     * Obtiene una paginación del servidor y la carga en el estado como
     * paginación actualmente mostrada al usuario.
     */
    loadPostPagination: async ({state, commit, dispatch}, filters?: PostPaginationFilters): Promise<PostPagination> => {
        commit(MutationTypes.SetPostPaginationLoading, true)
        // Solicita a la API una paginación de publicaciones
        const pagination = await api.getPostPagination(
            filters || state.currentPostPaginationFilters || {} as PostPaginationFilters,
        )

        commit(MutationTypes.SetCurrentPostPagination, pagination)
        commit(MutationTypes.SetPostPaginationLoading, false)

        return pagination
    },

    /**
     * Elimina un artículo
     *
     * @param context
     * @param payload
     */
    deletePost: async ({state, commit, dispatch}, payload = {updatePagination: false}) => {
        const id = state.postToDelete.id
        const result = await api.deletePost(id)

        if (payload.updatePagination) {
            await dispatch('loadPostPagination', state.currentPostPaginationFilters)
        } else {
            commit(MutationTypes.DeletePostInCurrentPagination, state.postToDelete)
        }

        // Resetea la publicación a eliminar
        commit(MutationTypes.SetPostToDelete, new Post())
        notify(`La publicación ${id} se ha eliminado correctamente`, Colors.Success)
    },

    /**
     * Carga la vista previa de la publicación actualmente seleccionada por el usuario
     */
    loadPostPreview: async ({state, commit}) => {
        commit(MutationTypes.SetLoadingPreview, true)

        const data = await api.getPostPreview(state.editingPost, state.currentTranslation)
        commit(MutationTypes.SetCurrentPostPreview, data.preview_data)
        commit(MutationTypes.SetLoadingPreview, false)
    },

    /**
     * Carga la lista de etiquetas registradas en la aplicación
     *
     * Si payload.force es true se volverá a solicitar al servidor la lista de etiquetas
     * aunque esta ya haya sido obtenida.
     */
    async loadTags ({state, commit}, payload = {force: false}) {
        if (state.tagsFetched && !payload.force) {
            return
        }

        const tags = await api.getTagList()
        commit(MutationTypes.SetTagList, tags)
        commit(MutationTypes.ToggleTagsFetched, true)
    },

    /**
     * Elimina la etiqueta actualmente seleccionada
     */
    async deleteTag ({state, commit}) {
        await api.deleteTag(state.tag.id)
        // Elimina la etiqueta de la lista cargada en la aplicación
        commit(MutationTypes.DeleteTag, state.tag)
        commit(MutationTypes.SetCurrentTag, new Tag())
    },

    /**
     * Crea una nueva etiqueta
     *
     * @param commit
     */
    createNewTag ({commit}) {
        commit(MutationTypes.SetCurrentTag, new Tag())
    },

    /**
     * Guarda los cambios de la etiqueta actualmente en edición por el usuario
     *
     * Si no existe será creada
     */
    async saveTag ({commit, state}) {
        let { tag } = state
        tag = tag.isNew ? await api.createTag(tag) : await api.updateTag(tag)

        commit(MutationTypes.SetCurrentTag, tag)
        commit(MutationTypes.AddTag, tag)
    },
}

export default actions
